import { useId, useState } from 'react'
import { CATEGORY_TYPES, grouped, type Catalog, type CategoryType, type NewCategory } from './lib/categories'

/** An existing category_id, or one to add to the Categories tab. */
export type CategoryChoice = string | NewCategory

const ADD = '\u0000add'
const PENDING = '\u0000pending'

const TYPE_WORDS: Record<CategoryType, string> = {
  spending: 'Spending',
  income: 'Income',
  transfer: 'Transfer between my accounts',
  investing: 'Investing',
}

export const choiceLabel = (c: CategoryChoice, cats: Catalog) => (typeof c === 'string' ? cats.label(c) : c.subcategory ? `${c.category} / ${c.subcategory}` : c.category)

/**
 * The category dropdown: every category alphabetically (see `grouped`),
 * parents with their subcategories together, and "Add a category…" at the end,
 * which opens a small form for a name, an optional subcategory and how it's
 * counted. `onAdding` hears when that form opens and closes.
 */
export function CategoryPicker({
  value,
  categories,
  disabled,
  label,
  onPick,
  onAdding,
}: {
  value: CategoryChoice
  categories: Catalog
  disabled?: boolean
  label: string
  onPick: (c: CategoryChoice) => void
  onAdding?: (adding: boolean) => void
}) {
  const [adding, setAddingState] = useState(false)
  const setAdding = (a: boolean) => {
    setAddingState(a)
    onAdding?.(a)
  }
  const known = typeof value === 'string' && categories.list.some((c) => c.id === value)
  return (
    <span className="category-picker">
      <select
        aria-label={label}
        value={adding ? ADD : typeof value === 'string' ? value : PENDING}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === ADD) return setAdding(true)
          setAdding(false)
          if (e.target.value !== PENDING) onPick(e.target.value)
        }}
      >
        {typeof value === 'string' && !known && <option value={value}>{categories.label(value)}</option>}
        {typeof value !== 'string' && <option value={PENDING}>{choiceLabel(value, categories)} (new)</option>}
        {grouped(categories).flatMap((g) => g.items.map((c) => <option key={c.id} value={c.id}>{categories.label(c.id)}</option>))}
        <option value={ADD}>Add a category…</option>
      </select>
      {adding && (
        <NewCategoryForm
          categories={categories}
          onSave={(c) => {
            setAdding(false)
            onPick(c)
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </span>
  )
}

/** Not a <form>: it can sit inside the rule form, and forms don't nest. */
function NewCategoryForm({ categories, onSave, onCancel }: { categories: Catalog; onSave: (c: NewCategory) => void; onCancel: () => void }) {
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [type, setType] = useState<CategoryType>('spending')
  const listId = useId()
  // A parent that already exists keeps its type unless changed.
  const parentType = categories.list.find((c) => c.category.toLowerCase() === category.trim().toLowerCase())?.type
  const ready = category.trim().length > 0
  const save = () => ready && onSave({ category: category.trim(), subcategory: subcategory.trim(), type })
  const enter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }
  return (
    <span className="new-category" role="group" aria-label="New category">
      <input
        autoFocus
        aria-label="Category"
        placeholder="Category, e.g. Cars"
        list={listId}
        value={category}
        onKeyDown={enter}
        onChange={(e) => {
          setCategory(e.target.value)
          const t = categories.list.find((c) => c.category.toLowerCase() === e.target.value.trim().toLowerCase())?.type
          if (t) setType(t)
        }}
      />
      <datalist id={listId}>
        {grouped(categories).map(({ group }) => <option key={group} value={group} />)}
      </datalist>
      <input aria-label="Subcategory" placeholder="Subcategory (optional)" value={subcategory} onKeyDown={enter} onChange={(e) => setSubcategory(e.target.value)} />
      <select aria-label="Counted as" value={type} onChange={(e) => setType(e.target.value as CategoryType)}>
        {CATEGORY_TYPES.map((t) => <option key={t} value={t}>{TYPE_WORDS[t]}</option>)}
      </select>
      <button type="button" disabled={!ready} onClick={save}>Add</button>
      <button type="button" className="link" onClick={onCancel}>Cancel</button>
      {parentType && parentType !== type && <span className="muted small-inline">Other {category.trim()} categories count as {TYPE_WORDS[parentType].toLowerCase()}.</span>}
    </span>
  )
}
